import { NgModule, ModuleWithProviders } from '@angular/core';

import { MediaQueryModule } from '@backbase/ui-ang/media-query-lib';
import { LayoutConfig, LAYOUT_CONFIG_TOKEN } from './bb-layout-lib/layout.model';
import { LayoutNavDropdownDirective } from './bb-layout-nav-dropdown-directive/layout-nav-dropdown.directive';

/**
 * Angular module providing comprehensive layout functionality for responsive applications.
 *
 * @remarks
 * This module provides the {@link LayoutService} for managing responsive layout behavior and
 * the {@link LayoutNavDropdownDirective} for navigation dropdown state management. It includes
 * a `forRoot()` method for configuring layout behavior at the application level.
 *
 * The {@link LayoutModule} should typically be imported using `forRoot()` in your root application
 * module to provide configuration for the entire application. Feature modules can import
 * the module directly without `forRoot()` to access the directives and services.
 *
 * @example
 * ```typescript
 * // In your root AppModule
 * import { LayoutModule } from '@backbase/ui-ang/layout';
 * import { MediaBreakpoints } from '@backbase/ui-ang/media-query-lib';
 *
 * @NgModule({
 *   imports: [
 *     BrowserModule,
 *     LayoutModule.forRoot({
 *       collapseBreakpoint: MediaBreakpoints.lg
 *     })
 *   ],
 *   // ... other module configuration
 * })
 * export class AppModule { }
 * ```
 */
@NgModule({
  imports: [MediaQueryModule],
  declarations: [LayoutNavDropdownDirective],
  exports: [LayoutNavDropdownDirective],
})
export class LayoutModule {
  /**
   * Configures the {@link LayoutModule} for the root application module with layout settings.
   *
   * @remarks
   * This static method should be called when importing {@link LayoutModule} in your root
   * application module. It provides the layout configuration to the {@link LayoutService}
   * throughout the entire application, setting responsive breakpoints and other
   * layout behavior options.
   * Only call `forRoot()` once in your root application module.
   *
   * @param config - The layout configuration object defining responsive behavior
   * @returns A ModuleWithProviders containing the {@link LayoutModule} and configuration providers
   *
   * @example
   * ```typescript
   * // Configure with specific breakpoint
   * import { LayoutModule } from '@backbase/ui-ang/layout';
   * import { MediaBreakpoints } from '@backbase/ui-ang/media-query-lib';
   *
   * @NgModule({
   *   imports: [
   *     LayoutModule.forRoot({
   *       collapseBreakpoint: MediaBreakpoints.md
   *     })
   *   ]
   * })
   * export class AppModule { }
   * ```
   */
  static forRoot(config: LayoutConfig): ModuleWithProviders<LayoutModule> {
    return { ngModule: LayoutModule, providers: [{ provide: LAYOUT_CONFIG_TOKEN, useValue: config }] };
  }
}
